/**
 * Deciding which bytes of a tensor to actually read.
 *
 * Small tensors are read whole. Large ones are sampled — and *how* they are
 * sampled is load-bearing for this product rather than an implementation
 * detail, because the headline statistic (row-norm p99÷p50, the structure
 * warning) is precisely the thing a careless sample destroys.
 *
 * Three rules, from the plan's R1-5 design note:
 *
 *   1. **Whole rows, never strided elements.** Striding by k over a matrix
 *      whose row length shares a factor with k lands on the same column every
 *      time. That is not a small bias — it is aliasing against exactly the
 *      column structure the tool exists to reveal. Sampling complete rows also
 *      makes each sampled row's norm *exact*, so the row statistic survives
 *      sampling as ordinary quantile estimation rather than becoming a guess.
 *
 *   2. **Pseudo-random offsets, not evenly spaced ones.** Even spacing has its
 *      own aliasing failure and it lands on the tensors we care most about:
 *      an attention projection of shape [32 heads × 128, d_model] sampled at 32
 *      evenly spaced offsets lands 128 rows apart — one run per head, at the
 *      same position within every head, perfectly phase-locked to the structure
 *      being measured. A PRNG breaks the phase.
 *
 *   3. **Deterministic given (sha, tensor).** Deep links promise "you see what
 *      I see". A sample redrawn on reload would break that promise invisibly —
 *      the histogram would just look slightly different. Seeding from the
 *      content hash makes the same link always read the same bytes.
 *
 * Rows are grouped into contiguous runs rather than fetched individually: each
 * range is an HTTP request, and 32 requests of 512 KiB cost far less than 4000
 * requests of 4 KiB.
 */

import { dtypeByteWidth } from "../safetensors/header.js";
import type { InventoryTensor } from "../safetensors/inventory.js";
import { mulberry32, seedFrom } from "../util/random.js";

/** Plan §5.3: fetch a tensor whole below this; sample above it. */
export const AUTO_FETCH_LIMIT_BYTES = 64 * 1024 * 1024;

/** Plan §5.3: a sampled read is at most this many separate ranges. */
export const MAX_SAMPLE_RANGES = 32;

/**
 * Bytes a sampled read aims to collect. Well below the auto-fetch limit — the
 * point of sampling is to be quick and cheap, and the statistics we compute
 * converge long before 16 MiB of a weight matrix.
 */
export const SAMPLE_BUDGET_BYTES = 16 * 1024 * 1024;

/**
 * Don't bother splitting a sample into a range smaller than this; per-request
 * overhead (headers, TLS record framing, a queue slot) starts to dominate.
 */
const MIN_RUN_BYTES = 256 * 1024;

export interface ByteRange {
  /** Absolute offset in the shard file. */
  readonly start: number;
  /** Exclusive. */
  readonly end: number;
}

/** A contiguous run of rows, in row units rather than bytes. */
export interface RowRun {
  readonly startRow: number;
  readonly rowCount: number;
}

export interface ReadPlan {
  readonly mode: "full" | "sampled";
  /**
   * The same selection as `ranges`, expressed in rows.
   *
   * Byte ranges are what the fetcher needs; row indices are what a *sibling*
   * plane needs. A quantized tensor is three tensors with different row widths
   * (packed weight, scales, biases) but the same row count, so sampling them
   * consistently means selecting the same row runs in each — impossible to do
   * from byte offsets computed for a different row width.
   */
  readonly rowRuns: readonly RowRun[];
  /** Absolute, non-overlapping, ascending — so the reads walk the file forward. */
  readonly ranges: readonly ByteRange[];
  /** Rows this plan will actually deliver. */
  readonly sampledRows: number;
  /** Rows the tensor has, after folding to 2-D. */
  readonly totalRows: number;
  /** Columns after folding — the row length in elements. */
  readonly cols: number;
  readonly bytes: number;
  /**
   * True when a single row exceeds the whole sample budget, so the plan had to
   * take fewer rows than it wanted. Statistics from such a plan rest on very
   * few rows and the UI should be correspondingly cautious.
   */
  readonly degenerate: boolean;
}

/**
 * Fold any shape to `[rows, cols]` by treating the last axis as the row.
 *
 * A 1-D tensor is one row. A 4-D convolution or an attention-bias of shape
 * `[1, 1, 1024, 1024]` folds to `[1024, 1024]`, which is the arrangement its
 * bytes are actually in — safetensors is row-major, so the last axis is
 * contiguous and everything before it is just row numbering.
 */
export function foldTo2D(shape: readonly number[]): { rows: number; cols: number } {
  if (shape.length === 0) return { rows: 1, cols: 1 };
  const cols = shape[shape.length - 1] ?? 1;
  const rows = shape.slice(0, -1).reduce((a, b) => a * b, 1);
  return { rows: Math.max(1, rows), cols: Math.max(1, cols) };
}

/**
 * Build the read plan for one tensor.
 *
 * `sha` is the repo's commit SHA — part of the seed so that two revisions of
 * the same tensor sample independently, and so a deep link pinned to a SHA
 * always reproduces its picture.
 */
export function planTensorRead(tensor: InventoryTensor, sha: string): ReadPlan {
  const { rows, cols } = foldTo2D(tensor.shape);
  const width = dtypeByteWidth(tensor.dtype);

  // Unknown dtype, or a tensor whose bytes don't divide into rows cleanly:
  // there is no safe way to slice it into rows, so read it whole or not at all.
  if (width === undefined || rows <= 1) {
    return fullPlan(tensor, rows, cols);
  }

  const rowBytes = cols * width;
  if (tensor.byteLength <= AUTO_FETCH_LIMIT_BYTES || rowBytes <= 0) {
    return fullPlan(tensor, rows, cols);
  }

  // How many rows fit the budget, and how to group them into runs.
  const rowsWanted = Math.max(
    1,
    Math.min(rows, Math.floor(SAMPLE_BUDGET_BYTES / rowBytes)),
  );
  const degenerate = rowsWanted < 2;

  const rowsPerRun = Math.max(1, Math.ceil(MIN_RUN_BYTES / rowBytes));
  const runCount = Math.max(
    1,
    Math.min(MAX_SAMPLE_RANGES, Math.floor(rowsWanted / rowsPerRun) || 1),
  );
  // Recompute so the runs actually sum to roughly the budget rather than
  // overshooting it after the clamps above.
  const perRun = Math.max(1, Math.min(rows, Math.floor(rowsWanted / runCount)));

  const starts = pickRunStarts(rows, perRun, runCount, seedFrom(sha, tensor.name));

  const rowRuns: RowRun[] = starts.map((startRow) => ({
    startRow,
    rowCount: Math.min(rows, startRow + perRun) - startRow,
  }));
  const ranges: ByteRange[] = rowRuns.map((run) => ({
    start: tensor.absoluteStart + run.startRow * rowBytes,
    end: tensor.absoluteStart + (run.startRow + run.rowCount) * rowBytes,
  }));

  const bytes = ranges.reduce((sum, r) => sum + (r.end - r.start), 0);

  return {
    mode: "sampled",
    ranges,
    rowRuns,
    sampledRows: Math.round(bytes / rowBytes),
    totalRows: rows,
    cols,
    bytes,
    degenerate,
  };
}

function fullPlan(tensor: InventoryTensor, rows: number, cols: number): ReadPlan {
  return {
    mode: "full",
    ranges: [{ start: tensor.absoluteStart, end: tensor.absoluteEnd }],
    rowRuns: [{ startRow: 0, rowCount: rows }],
    sampledRows: rows,
    totalRows: rows,
    cols,
    bytes: tensor.byteLength,
    degenerate: false,
  };
}

/**
 * Choose `count` non-overlapping run start rows, deterministically.
 *
 * Rejection sampling against already-chosen runs, with a bounded attempt count
 * so a pathological case (few possible positions, many runs) degrades to fewer
 * runs rather than spinning. Sorted ascending on the way out so the resulting
 * byte ranges walk the file forwards.
 */
function pickRunStarts(
  totalRows: number,
  perRun: number,
  count: number,
  seed: number,
): number[] {
  const lastStart = Math.max(0, totalRows - perRun);
  if (lastStart === 0) return [0];

  const random = mulberry32(seed);
  const chosen: number[] = [];
  const maxAttempts = count * 12;

  for (let attempt = 0; attempt < maxAttempts && chosen.length < count; attempt++) {
    const candidate = Math.floor(random() * (lastStart + 1));
    const overlaps = chosen.some(
      (existing) => candidate < existing + perRun && existing < candidate + perRun,
    );
    if (!overlaps) chosen.push(candidate);
  }

  // Nothing fit (very small tensor, large runs) — one run from the top is still
  // an honest sample, and the caller reports it as sampled either way.
  if (chosen.length === 0) chosen.push(0);

  return chosen.sort((a, b) => a - b);
}
